import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout, GlassCard, LoadingSpinner, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, Target, CheckCircle, Clock, ThumbsUp, ThumbsDown,
  ChevronRight, Lock, GraduationCap, Wrench, Shield, Rocket, Briefcase,
  AlertCircle, Eye, MessageSquare, HelpCircle, Scale, Unlock, ArrowRight,
  AlertTriangle, Undo2, X
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Progress } from '../components/ui/progress';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Milestone definitions
const MILESTONES = [
  { id: 'exploration', label: 'Exploration', icon: Target, description: 'Researching options' },
  { id: 'shortlist', label: 'Shortlist', icon: Users, description: 'Narrowed choices' },
  { id: 'lock', label: 'Lock', icon: Lock, description: 'Committed to path' },
  { id: 'apply', label: 'Apply', icon: CheckCircle, description: 'Applications sent' },
  { id: 'accepted', label: 'Accepted', icon: ThumbsUp, description: 'Received acceptance' },
  { id: 'commit', label: 'Commit', icon: Lock, description: 'Deposit paid' },
  { id: 'enroll', label: 'Enroll', icon: GraduationCap, description: 'Officially enrolled' }
];

// Path configurations
const PATH_CONFIGS = {
  college: { icon: GraduationCap, label: 'College Bound', gradient: 'from-blue-600 to-cyan-600' },
  trade: { icon: Wrench, label: 'Skilled Trades', gradient: 'from-amber-600 to-orange-600' },
  military: { icon: Shield, label: 'Military Service', gradient: 'from-green-600 to-emerald-600' },
  entrepreneurship: { icon: Rocket, label: 'Entrepreneurship', gradient: 'from-purple-600 to-pink-600' },
  workforce: { icon: Briefcase, label: 'Direct Workforce', gradient: 'from-slate-600 to-zinc-600' }
};

// Agreement status colors
const AGREEMENT_COLORS = {
  pending: { bg: 'bg-slate-500/20', text: 'text-slate-300', label: 'Pending' },
  student_confirmed: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Student Confirmed' },
  parent_support: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'You Support This' },
  parent_consider: { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Under Consideration' },
  parent_not_approved: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Not Approved' },
  agreed: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Family Agreed' },
  conflict: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Conflict' }
};

// Confirmation Dialog
const ConfirmDialog = ({ open, title, message, confirmLabel, onConfirm, onCancel, variant = 'default' }) => {
  if (!open) return null;
  const btnClass = variant === 'danger' 
    ? 'bg-red-600 hover:bg-red-700' 
    : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className="fixed inset-0 bg-[#0a1729]/60 z-50 flex items-center justify-center p-4" data-testid="confirm-dialog">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full shadow-2xl"
      >
        <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
        <p className="text-slate-300 text-sm mb-6">{message}</p>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onCancel} className="border-slate-600 text-slate-300" data-testid="confirm-cancel-btn">
            Cancel
          </Button>
          <Button onClick={onConfirm} className={btnClass} data-testid="confirm-ok-btn">
            {confirmLabel}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// Milestone Timeline Component
const MilestoneTimeline = ({ currentMilestone, pathId, onAdvance, onRevert, isParent }) => {
  const currentIndex = MILESTONES.findIndex(m => m.id === currentMilestone);
  
  return (
    <div className="relative">
      {/* Progress Bar */}
      <div className="absolute top-6 left-0 right-0 h-1 bg-slate-700">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500"
          style={{ width: `${(currentIndex / (MILESTONES.length - 1)) * 100}%` }}
        />
      </div>
      
      {/* Milestone Nodes */}
      <div className="flex justify-between relative">
        {MILESTONES.map((milestone, index) => {
          const Icon = milestone.icon;
          const isComplete = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isNext = index === currentIndex + 1;
          
          return (
            <div key={milestone.id} className="flex flex-col items-center">
              <div
                data-testid={`milestone-${milestone.id}`}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all z-10 ${
                  isComplete ? 'bg-emerald-500 text-white' :
                  isCurrent ? 'bg-blue-500 text-white ring-4 ring-blue-500/30' :
                  isNext ? 'bg-slate-700 text-slate-300' :
                  'bg-slate-800 text-slate-300'
                }`}
              >
                {isComplete ? <CheckCircle className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
              </div>
              <p className={`mt-2 text-xs font-medium ${
                isCurrent ? 'text-blue-400' : isComplete ? 'text-emerald-400' : 'text-slate-300'
              }`}>
                {milestone.label}
              </p>
              <p className="text-xs text-slate-300 hidden md:block">{milestone.description}</p>
            </div>
          );
        })}
      </div>
      
      {/* Info for parents - they can view but not modify milestones */}
      {isParent && (
        <div className="mt-4 text-center">
          <p className="text-slate-300 text-sm">
            Your child controls milestone progression. You can provide feedback on their decisions below.
          </p>
        </div>
      )}
    </div>
  );
};

// Path Decision Card - Full Version with Family Agreement
const PathDecisionCard = ({ 
  path, 
  childName,
  milestone, 
  agreement, 
  isParent,
  onParentResponse, 
  onUndoParentResponse,
  confirmDialog,
  setConfirmDialog
}) => {
  const PathIcon = PATH_CONFIGS[path.path_type]?.icon || Target;
  const pathConfig = PATH_CONFIGS[path.path_type];
  const agreementStatus = AGREEMENT_COLORS[agreement?.status] || AGREEMENT_COLORS.pending;
  const currentMilestone = milestone?.current_milestone || 'exploration';
  
  // Request parent response with confirmation
  const requestParentResponse = (stance) => {
    const stanceLabels = { support: 'Support', consider: 'Consider', not_approved: 'Not Approve' };
    setConfirmDialog({
      open: true,
      title: `${stanceLabels[stance]} this path?`,
      message: `You're about to mark "${path.institution_name}" as "${stanceLabels[stance]}". ${childName} will be notified of your decision.`,
      confirmLabel: stanceLabels[stance],
      variant: stance === 'not_approved' ? 'danger' : 'default',
      onConfirm: () => {
        setConfirmDialog({ open: false });
        onParentResponse(path.id, stance);
      }
    });
  };
  
  return (
    <GlassCard className="p-6" data-testid={`path-decision-card-${path.id}`}>
      {/* Path Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br ${pathConfig?.gradient || 'from-slate-600 to-slate-700'}`}>
            <PathIcon className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-slate-300 text-sm">{childName}</p>
            <h3 className="text-xl font-bold text-white">{path.institution_name || 'Unnamed Path'}</h3>
            <p className="text-slate-300 capitalize">
              {pathConfig?.label || path.path_type} 
              {path.major_or_track ? ` • ${path.major_or_track}` : ''}
            </p>
          </div>
        </div>
        <Badge className={`${agreementStatus.bg} ${agreementStatus.text} border-none`}>
          {agreementStatus.label}
        </Badge>
      </div>
      
      {/* Milestone Timeline */}
      <div className="mb-6">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Target className="w-4 h-4 text-blue-400" />
          Decision Progress
        </h4>
        <MilestoneTimeline 
          currentMilestone={currentMilestone}
          pathId={path.id}
          isParent={isParent}
        />
      </div>
      
      {/* Family Agreement Section */}
      <div className="border-t border-slate-700 pt-6">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-400" />
          Family Agreement
        </h4>
        
        {/* Student Status */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50 mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              agreement?.student_confirmed ? 'bg-emerald-500/20' : 'bg-slate-700'
            }`}>
              {agreement?.student_confirmed ? (
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              ) : (
                <HelpCircle className="w-5 h-5 text-slate-300" />
              )}
            </div>
            <div>
              <p className="text-white font-medium">{childName} (Student)</p>
              <p className="text-slate-300 text-sm">
                {agreement?.student_confirmed ? 'Has confirmed this path' : 'Has not yet confirmed'}
              </p>
            </div>
          </div>
        </div>
        
        {/* Parent Responses Display */}
        {agreement?.parent_responses?.length > 0 ? (
          <div className="space-y-2 mb-4">
            {agreement.parent_responses.map((response, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    response.stance === 'support' ? 'bg-emerald-500/20' :
                    response.stance === 'not_approved' ? 'bg-red-500/20' :
                    'bg-amber-500/20'
                  }`}>
                    {response.stance === 'support' ? (
                      <ThumbsUp className="w-5 h-5 text-emerald-400" />
                    ) : response.stance === 'not_approved' ? (
                      <ThumbsDown className="w-5 h-5 text-red-400" />
                    ) : (
                      <HelpCircle className="w-5 h-5 text-amber-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">{response.parent_name || 'You'}</p>
                    <p className="text-slate-300 text-sm capitalize">{response.stance?.replace('_', ' ')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {response.notes && (
                    <p className="text-slate-300 text-sm max-w-xs truncate">{response.notes}</p>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => onUndoParentResponse(path.id)}
                    className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                    data-testid="change-response-btn"
                  >
                    <Undo2 className="w-3 h-3 mr-1" /> Change
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : agreement?.student_confirmed ? (
          /* Show response options when student has confirmed but parent hasn't responded */
          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-4">
            <p className="text-blue-400 font-medium mb-3">
              {childName} has selected this path. What's your stance?
            </p>
            <div className="flex flex-wrap gap-2">
              <Button 
                onClick={() => requestParentResponse('support')}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="parent-support-btn"
              >
                <ThumbsUp className="w-4 h-4 mr-2" /> Support
              </Button>
              <Button 
                onClick={() => requestParentResponse('consider')}
                variant="outline"
                className="border-amber-500 text-amber-400 hover:bg-amber-500/10"
                data-testid="parent-consider-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" /> Need to Consider
              </Button>
              <Button 
                onClick={() => requestParentResponse('not_approved')}
                variant="outline"
                className="border-red-500 text-red-400 hover:bg-red-500/10"
                data-testid="parent-not-approved-btn"
              >
                <ThumbsDown className="w-4 h-4 mr-2" /> Not Approved
              </Button>
            </div>
          </div>
        ) : (
          /* Waiting for student to confirm */
          <div className="p-4 rounded-lg bg-slate-800/30 text-center mb-4">
            <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-300">Waiting for {childName} to confirm this path selection</p>
          </div>
        )}
        
        {/* Conflict Alert */}
        {agreement?.has_conflict && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 font-medium">Family Disagreement Detected</span>
            </div>
            <p className="text-slate-300 text-sm">
              There's a disagreement about this path. Consider having a family discussion to understand each perspective.
            </p>
            <Button className="mt-3 bg-red-600 hover:bg-red-700" size="sm">
              <MessageSquare className="w-4 h-4 mr-2" /> Start Discussion
            </Button>
          </div>
        )}
        
        {/* All Agreed Badge */}
        {agreement?.status === 'agreed' && (
          <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-center">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <p className="text-emerald-400 font-medium">Family Agreement Reached!</p>
            </div>
          </div>
        )}
      </div>
    </GlassCard>
  );
};

// Family Overview Summary
const FamilyOverviewCard = ({ children, allDecisions, allAgreements }) => {
  const totalPending = Object.values(allAgreements).reduce((sum, a) => {
    const needsResponse = a?.student_confirmed && (!a?.parent_responses || a.parent_responses.length === 0);
    return sum + (needsResponse ? 1 : 0);
  }, 0);
  
  const totalAgreed = Object.values(allAgreements).filter(a => a?.status === 'agreed').length;
  const totalChildren = children.length;
  const childrenWithPaths = children.filter(c => c.paths && c.paths.length > 0).length;
  
  return (
    <GlassCard className="p-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/30">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Users className="w-5 h-5 text-blue-400" />
        Family Decision Overview
      </h3>
      <div className="grid grid-cols-4 gap-4">
        <div className="text-center p-3 rounded-lg bg-slate-800/50">
          <p className="text-3xl font-bold text-white">{totalChildren}</p>
          <p className="text-slate-300 text-sm">Children</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-slate-800/50">
          <p className="text-3xl font-bold text-blue-400">{childrenWithPaths}</p>
          <p className="text-slate-300 text-sm">With Paths</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-slate-800/50">
          <p className={`text-3xl font-bold ${totalPending > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
            {totalPending}
          </p>
          <p className="text-slate-300 text-sm">Awaiting You</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-slate-800/50">
          <p className="text-3xl font-bold text-emerald-400">{totalAgreed}</p>
          <p className="text-slate-300 text-sm">Agreed</p>
        </div>
      </div>
      
      {totalPending > 0 && (
        <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <p className="text-amber-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {totalPending} path{totalPending > 1 ? 's' : ''} need{totalPending === 1 ? 's' : ''} your response
          </p>
        </div>
      )}
    </GlassCard>
  );
};

// Main Component
export default function FamilyDecisionWorkflow() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('all');
  const [milestones, setMilestones] = useState({});
  const [agreements, setAgreements] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ open: false });

  useEffect(() => {
    fetchData();
    // Live sync, re-poll every 20s so when the student soft-locks / commits
    // the parent's view updates without manual refresh.
    const t = setInterval(fetchData, 20_000);
    // Also refresh when the tab becomes visible again
    const onVis = () => { if (document.visibilityState === 'visible') fetchData(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch children
      const childrenRes = await axios.get(`${API}/family/children`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      const childrenData = childrenRes.data.children || [];
      
      // Fetch paths and agreements for each child
      const enrichedChildren = [];
      const allMilestones = {};
      const allAgreements = {};
      
      for (const child of childrenData) {
        try {
          // Get child's paths
          const pathsRes = await axios.get(`${API}/paths/child/${child.id}`, {
            ...getAuthHeader(),
            withCredentials: true
          });
          const childPaths = pathsRes.data.paths || [];
          
          // Get milestones for this child
          try {
            const milestonesRes = await axios.get(`${API}/decisions/milestones/${child.id}`, {
              ...getAuthHeader(),
              withCredentials: true
            });
            for (const m of milestonesRes.data.milestones || []) {
              allMilestones[m.path_id] = m;
            }
          } catch (e) {
            console.log(`No milestones for ${child.name}`);
          }
          
          // Get agreements for each path
          for (const path of childPaths) {
            try {
              const agreementRes = await axios.get(`${API}/decisions/agreements/${path.id}`, {
                ...getAuthHeader(),
                withCredentials: true
              });
              if (agreementRes.data.agreement) {
                allAgreements[path.id] = agreementRes.data.agreement;
              }
            } catch (e) {
              // No agreement yet
            }
          }
          
          enrichedChildren.push({
            ...child,
            paths: childPaths
          });
        } catch (e) {
          console.log(`Failed to load paths for ${child.name}`);
          enrichedChildren.push({ ...child, paths: [] });
        }
      }
      
      setChildren(enrichedChildren);
      setMilestones(allMilestones);
      setAgreements(allAgreements);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load family data');
    } finally {
      setLoading(false);
    }
  };

  // Handle parent response
  const handleParentResponse = async (pathId, stance) => {
    try {
      await axios.post(`${API}/decisions/agreements/${pathId}/parent-response`, {
        stance,
        parent_name: user?.full_name,
        notes: ''
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      
      toast.success(
        stance === 'support' ? 'You\'ve supported this path!' :
        stance === 'consider' ? 'Marked for consideration' :
        'Response recorded',
        {
          action: {
            label: 'Undo',
            onClick: () => handleUndoParentResponse(pathId)
          }
        }
      );
      
      fetchData();
    } catch (error) {
      showFriendlyError(error, { fallback: 'Failed to submit response' });
    }
  };

  // Undo parent response
  const handleUndoParentResponse = async (pathId) => {
    try {
      await axios.post(`${API}/decisions/agreements/${pathId}/undo-parent-response`, {}, {
        ...getAuthHeader(),
        withCredentials: true
      });
      toast.success('Response withdrawn');
      fetchData();
    } catch (error) {
      toast.error('Failed to undo response');
    }
  };

  // Filter children based on selection
  const filteredChildren = selectedChildId === 'all' 
    ? children 
    : children.filter(c => c.id === selectedChildId);

  // Get all paths from filtered children
  const allFilteredPaths = filteredChildren.flatMap(child => 
    (child.paths || []).map(path => ({ ...path, childName: child.name, childId: child.id }))
  );

  return (
    <AppLayout>
      <div className="space-y-6">
        <PageHeader 
          title="Family Decision Workflow"
          description="Track and support your children's educational path decisions. Review their progress and provide your input."
        />
        
        {/* How It Works Explanation */}
        <GlassCard className="p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/20">
          <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-400" />
            How the Family Decision Workflow Works
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Your Role as Parent */}
            <div className="space-y-3">
              <h4 className="text-purple-400 font-semibold flex items-center gap-2">
                <Users className="w-4 h-4" /> Your Role as Parent
              </h4>
              <div className="text-slate-300 text-sm space-y-2">
                <p><strong className="text-white">Review & Respond:</strong> Your children select their paths. You provide guidance and feedback.</p>
                <p><strong className="text-emerald-400">Support:</strong> You fully support this path choice.</p>
                <p><strong className="text-amber-400">Need to Consider:</strong> You're open but want to discuss more first.</p>
                <p><strong className="text-red-400">Not Approved:</strong> You have concerns that need to be addressed.</p>
                <p className="mt-2 text-white/80">Your input helps guide better decisions together as a family.</p>
              </div>
            </div>
            
            {/* The 7-Stage Journey */}
            <div className="space-y-3">
              <h4 className="text-blue-400 font-semibold flex items-center gap-2">
                <Target className="w-4 h-4" /> The 7-Stage Journey
              </h4>
              <div className="text-slate-300 text-sm space-y-2">
                <p><strong className="text-white">1. Exploration:</strong> Research and discover potential paths.</p>
                <p><strong className="text-white">2. Shortlist:</strong> Narrow down to top choices.</p>
                <p><strong className="text-white">3. Lock:</strong> Commit to seriously pursuing this path.</p>
                <p><strong className="text-white">4. Apply:</strong> Submit applications.</p>
                <p><strong className="text-white">5. Accepted:</strong> Received an acceptance.</p>
                <p><strong className="text-white">6. Commit:</strong> Make deposit and confirm.</p>
                <p><strong className="text-white">7. Enroll:</strong> Officially enrolled!</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <p className="text-slate-300 text-sm">
              <strong className="text-amber-400">Note:</strong> Your child controls milestone progression. You can view their progress and provide feedback on their decisions. Changed your mind? Every response has a <strong className="text-white">Change</strong> button.
            </p>
          </div>
        </GlassCard>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : children.length === 0 ? (
          <GlassCard className="p-12 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Children Linked</h2>
            <p className="text-slate-300 mb-4">Add children to your family to track their educational journey.</p>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/app/family/settings')}>
              Add Child
            </Button>
          </GlassCard>
        ) : (
          <>
            {/* Family Overview */}
            <FamilyOverviewCard 
              children={children} 
              allDecisions={milestones}
              allAgreements={agreements}
            />
            
            {/* Child Filter Tabs */}
            <Tabs value={selectedChildId} onValueChange={setSelectedChildId}>
              <TabsList className="bg-slate-800/50 p-1">
                <TabsTrigger 
                  value="all" 
                  className="data-[state=active]:bg-blue-600"
                >
                  All Children ({children.length})
                </TabsTrigger>
                {children.map(child => {
                  const pendingCount = (child.paths || []).filter(p => {
                    const a = agreements[p.id];
                    return a?.student_confirmed && (!a?.parent_responses || a.parent_responses.length === 0);
                  }).length;
                  
                  return (
                    <TabsTrigger 
                      key={child.id} 
                      value={child.id}
                      className="data-[state=active]:bg-blue-600 relative"
                    >
                      {child.name?.split(' ')[0]}
                      {pendingCount > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                          {pendingCount}
                        </span>
                      )}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </Tabs>
            
            {/* Path Decision Cards */}
            {allFilteredPaths.length === 0 ? (
              <GlassCard className="p-8 text-center">
                <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-white mb-2">No Paths Selected</h3>
                <p className="text-slate-300">
                  {selectedChildId === 'all' 
                    ? 'None of your children have selected paths yet.'
                    : 'This child hasn\'t selected any paths yet.'}
                </p>
              </GlassCard>
            ) : (
              <div className="grid lg:grid-cols-2 gap-6">
                {allFilteredPaths.map(path => (
                  <PathDecisionCard
                    key={path.id}
                    path={path}
                    childName={path.childName}
                    milestone={milestones[path.id]}
                    agreement={agreements[path.id]}
                    isParent={true}
                    onParentResponse={handleParentResponse}
                    onUndoParentResponse={handleUndoParentResponse}
                    confirmDialog={confirmDialog}
                    setConfirmDialog={setConfirmDialog}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false })}
      />
    </AppLayout>
  );
}
